import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, CheckCircle, Crown, ArrowRight, Eye, Rocket, Music, Image, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PacksPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const tx = (es, en, pt) => isEs ? es : (isPt ? (pt || en) : en);
  const [packs, setPacks] = useState([]);
  const [ultimatePack, setUltimatePack] = useState(null);
  const [artistLaunch, setArtistLaunch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const [packsRes, ultimateRes, artistRes] = await Promise.all([
        axios.get(`${API_URL}/api/packs/`),
        axios.get(`${API_URL}/api/packs/ultimate`),
        axios.get(`${API_URL}/api/packs/artist-launch`)
      ]);
      setPacks(packsRes.data);
      setUltimatePack(ultimateRes.data);
      setArtistLaunch(artistRes.data);
    } catch (error) {
      console.error('Error fetching packs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyPack = (packId) => {
    navigate(`/checkout?type=pack&id=${packId}`);
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="container-custom py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
            {t('packs.overline')}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {t('packs.title')} <span className="gold-text">{t('packs.titleHighlight')}</span>
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-6">
            Compra packs de 5, 10 o 20 tracks · Ahorra hasta 40% comprando en bundle
          </p>

          {/* Mastering reassurance banner — primary trust signal */}
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-green-500/15 ring-1 ring-green-500/30 mb-4" data-testid="packs-mastering-badge">
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-200 font-semibold">
              Toda la música YA viene masterizada profesionalmente · Lista para Spotify, Apple Music y radio
            </p>
          </div>
          <p className="text-xs text-white/40 max-w-xl mx-auto">
            Cada track de cada pack pasa por nuestro proceso de mastering nivel estudio (-14 LUFS · WAV 24-bit) antes de llegar a ti. Compra → Descarga → Distribuye. Sin retoques, sin esperas.
          </p>
        </div>

        {/* Regular Packs */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-8">All Packs</h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-6 animate-pulse">
                <div className="aspect-video bg-white/5 rounded-lg mb-4" />
                <div className="h-6 bg-white/5 rounded mb-2" />
                <div className="h-4 bg-white/5 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {packs.map((pack) => (
              <div 
                key={pack.id}
                className="glass-card overflow-hidden group hover:-translate-y-1 transition-transform"
                data-testid={`pack-card-${pack.id}`}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={pack.cover_image}
                    alt={pack.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <span className="px-3 py-1 text-xs font-medium bg-white/10 backdrop-blur-sm rounded-full">
                      {pack.track_count} {t('packs.tracks')}
                    </span>
                    <span className="px-3 py-1 text-xs font-medium bg-white/10 backdrop-blur-sm rounded-full">
                      {pack.genre}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{pack.name}</h3>
                  <p className="text-sm text-white/50 mb-3 line-clamp-2">{pack.description}</p>

                  {/* Pre-mastered badge — visible on every pack card */}
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-green-500/10 ring-1 ring-green-500/25 text-green-300 text-[11px] font-semibold" data-testid={`pack-mastered-badge-${pack.id}`}>
                    <CheckCircle className="w-3 h-3" />
                    Pre-masterizado · Listo para Spotify
                  </div>

                  {/* Playlist ecosystem note */}
                  <div className="flex items-center gap-2 mb-4 text-xs text-[#E8720C]/80">
                    <Eye className="w-3.5 h-3.5" />
                    <span>Playlist ecosystem eligible after distribution</span>
                  </div>
                  
                  {/* Formats */}
                  <div className="flex gap-2 mb-4">
                    {pack.formats?.map((format) => (
                      <span 
                        key={format}
                        className="px-2 py-1 text-xs font-mono bg-white/5 rounded"
                      >
                        {format}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <span className="text-2xl font-bold gold-text">${pack.price}</span>
                    <button 
                      onClick={() => handleBuyPack(pack.id)}
                      className="btn-secondary py-2 px-6 text-sm flex items-center gap-2"
                      data-testid={`pack-buy-${pack.id}`}
                    >
                      {t('packs.buyPack')}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ultimate Pack Feature */}
        {ultimatePack && (
          <div className="glass-card p-8 md:p-12 border-[#E8720C]/20 mb-16 relative overflow-hidden" data-testid="ultimate-pack-section">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/10 via-transparent to-[#E8720C]/10 blur-3xl" />
            
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-black rounded-full mb-6">
                  <Crown className="w-3 h-3" />
                  {t('packs.ultimateOverline')}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  {ultimatePack.name}
                </h2>
                <p className="text-lg text-white/60 mb-8">
                  {t('packs.ultimateDesc')}
                </p>
                
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {(Array.isArray(ultimatePack.features) ? ultimatePack.features : []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/70">
                      <CheckCircle className="w-5 h-5 text-[#E8720C] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center gap-2 mb-8 text-sm text-[#E8720C]/80">
                  <Eye className="w-4 h-4" />
                  <span>Curated playlist exposure after release &bull; Visibility opportunities</span>
                </div>

                <div className="flex items-center gap-6">
                  <div>
                    <span className="text-5xl font-bold gold-text">${ultimatePack.price}</span>
                    <span className="text-white/40 ml-2">one-time</span>
                  </div>
                  <button 
                    onClick={() => handleBuyPack('ultimate')}
                    className="btn-gold"
                    data-testid="ultimate-pack-buy"
                  >
                    {t('packs.getUltimate')}
                  </button>
                </div>
              </div>

              <div className="relative">
                <img 
                  src={ultimatePack.cover_image}
                  alt={ultimatePack.name}
                  className="w-full rounded-2xl animate-float"
                />
              </div>
            </div>
          </div>
        )}

        {/* Artist Launch System */}
        {artistLaunch && (
          <div className="glass-card p-8 md:p-12 border-white/10 relative overflow-hidden" data-testid="artist-launch-section">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128]/50 via-transparent to-[#E8720C]/20 blur-3xl" />
            
            <div className="relative">
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 px-4 py-1 text-xs font-bold uppercase tracking-wider bg-white text-black rounded-full mb-6">
                  <Rocket className="w-3 h-3" />
                  {t('artistLaunch.overline')}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  {t('artistLaunch.name')}
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto">
                  {t('artistLaunch.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="glass-card p-6">
                  <Music className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">Music Package</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.features')) ? t('artistLaunch.features') : []).slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="glass-card p-6">
                  <Image className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{t('artistLaunch.visualIdentity')}</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.visualFeatures')) ? t('artistLaunch.visualFeatures') : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="glass-card p-6">
                  <BarChart3 className="w-10 h-10 text-[#E8720C] mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{t('artistLaunch.distribution')}</h3>
                  <ul className="space-y-2">
                    {(Array.isArray(t('artistLaunch.distFeatures')) ? t('artistLaunch.distFeatures') : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-[#E8720C] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <div className="flex items-baseline justify-center gap-3 mb-2">
                  <span className="text-5xl font-bold gold-text">$399.99</span>
                  <span className="text-white/40">one-time</span>
                </div>
                <p className="text-sm text-white/40 mb-6">Price range: $300 – $500+</p>
                <button 
                  onClick={() => handleBuyPack('artist-launch')}
                  className="btn-primary text-lg px-12"
                  data-testid="artist-launch-buy"
                >
                  <Rocket className="w-5 h-5 mr-2 inline" />
                  {t('artistLaunch.cta')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PacksPage;
